import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { renderJobResponsibilities } from "../../../utils/textUtils";
import { Card, Tag, Button, Spin, message, Row, Col, Divider } from "antd";
import {
  ArrowLeftOutlined,
  DollarOutlined,
  CalendarOutlined,
  EditOutlined,
  EnvironmentOutlined,
  ToolOutlined,
  FileTextOutlined,
  LinkOutlined,
  FolderOutlined,
} from "@ant-design/icons";
import { ViewJobDetailsWrapper } from "./ViewJobDetails.style";
import axios from "axios";
import { API_CONST } from "../../../const";

const CustomerJobFullDetails = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [jobData, setJobData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        const response = await axios.get(`${API_CONST.GET_JOB_REQUIREMENT}/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (response.data.job) {
          setJobData(response.data.job);
        }
      } catch (error) {
        console.error("Error fetching job details:", error);
        message.error("Failed to load job details");
      } finally {
        setLoading(false);
      }
    };

    if (jobId) {
      fetchJobDetails();
    }
  }, [jobId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return `₹ ${Number(amount).toLocaleString('en-IN')}`;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open': return 'green';
      case 'closed': return 'red';
      case 'filled': return 'blue';
      case 'draft': return 'orange';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <ViewJobDetailsWrapper>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <Spin size="large" />
          <p style={{ marginTop: 16 }}>Loading job details...</p>
        </div>
      </ViewJobDetailsWrapper>
    );
  }

  if (!jobData) {
    return (
      <ViewJobDetailsWrapper>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <h2>Job not found</h2>
          <Button type="primary" onClick={() => navigate("/customer/my-jobs")}>
            Back to My Jobs
          </Button>
        </div>
      </ViewJobDetailsWrapper>
    );
  }

  return (
    <ViewJobDetailsWrapper>
      <div className="header">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(`/customer/view-job/${jobId}`)}
          className="back-button"
        >
          Back to Job Summary
        </Button>
        <div className="title-row">
          <h2>Complete Job Details</h2>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/customer/edit-job/${jobId}`)}
            style={{
              backgroundColor: "#00d9a9",
              borderColor: "#00d9a9",
              height: "40px",
            }}
          >
            Edit Job
          </Button>
        </div>
      </div>

      <Card className="job-details-card" style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <div className="job-id">Job ID: {jobData.id}</div>
            <h1 className="job-title" style={{ margin: "8px 0" }}>{jobData.role}</h1>
            <div className="company-info">
              <Tag color={getStatusColor(jobData.status)}>{jobData.status?.toUpperCase()}</Tag>
              <Tag color="blue">{jobData.engagementType}</Tag>
              <Tag color="cyan" icon={<EnvironmentOutlined />}>{jobData.reportingLocation}</Tag>
            </div>
          </div>
        </div>
      </Card>

      <Card title={<><LinkOutlined style={{ marginRight: 8 }} />Engagement & Project</>} style={{ marginBottom: 24 }}>
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12}>
            <div className="detail-item">
              <span className="detail-label">Engagement</span>
              {jobData.engagement ? (
                <span 
                  className="detail-value" 
                  style={{ color: '#1890ff', cursor: 'pointer' }}
                  onClick={() => navigate(`/customer/engagement/${jobData.engagementId}`)}
                >
                  {jobData.engagement.title}
                </span>
              ) : (
                <span className="detail-value" style={{ color: '#8c8c8c' }}>NA</span>
              )}
            </div>
          </Col>
          <Col xs={24} sm={12}>
            <div className="detail-item">
              <span className="detail-label">Project</span>
              {jobData.project ? (
                <span 
                  className="detail-value" 
                  style={{ color: '#1890ff', cursor: 'pointer' }}
                  onClick={() => navigate(`/customer/project/${jobData.projectId}`)}
                >
                  <FolderOutlined style={{ marginRight: 4 }} />
                  {jobData.project.title}
                </span>
              ) : (
                <span className="detail-value" style={{ color: '#8c8c8c' }}>NA</span>
              )}
            </div>
          </Col>
        </Row>
      </Card>

      <Card title={<><FileTextOutlined style={{ marginRight: 8 }} />Basic Details</>} style={{ marginBottom: 24 }}>
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={8}>
            <div className="detail-item">
              <span className="detail-label">Job Title / Role</span>
              <span className="detail-value">{jobData.role || '-'}</span>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div className="detail-item">
              <span className="detail-label">Experience Range</span>
              <span className="detail-value">{jobData.experienceRange || '-'}</span>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div className="detail-item">
              <span className="detail-label">Primary Skills</span>
              <div style={{ marginTop: 4 }}>
                {(jobData.primarySkills || []).map((skill, index) => (
                  <Tag key={index} color="blue" style={{ marginBottom: 4 }}>
                    {skill}
                  </Tag>
                ))}
                {(!jobData.primarySkills || jobData.primarySkills.length === 0) && (
                  <span style={{ color: "#8c8c8c" }}>None specified</span>
                )}
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div className="detail-item">
              <span className="detail-label">Secondary Skills</span>
              <div style={{ marginTop: 4 }}>
                {(jobData.secondarySkills || []).map((skill, index) => (
                  <Tag key={index} color="green" style={{ marginBottom: 4 }}>
                    {skill}
                  </Tag>
                ))}
                {(!jobData.secondarySkills || jobData.secondarySkills.length === 0) && (
                  <span style={{ color: "#8c8c8c" }}>None specified</span>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      <Card title={<><DollarOutlined style={{ marginRight: 8 }} />Budget & Engagement</>} style={{ marginBottom: 24 }}>
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={6}>
            <div className="detail-item">
              <span className="detail-label">Budget</span>
              <span className="detail-value">{formatCurrency(jobData.budget)}</span>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div className="detail-item">
              <span className="detail-label">Budget Period</span>
              <span className="detail-value">{jobData.budgetPeriod || '-'}</span>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div className="detail-item">
              <span className="detail-label">Engagement Duration</span>
              <span className="detail-value">{jobData.monthsOfEngagement || '-'}</span>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div className="detail-item">
              <span className="detail-label">Engagement Type</span>
              <span className="detail-value">{jobData.engagementType || '-'}</span>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div className="detail-item">
              <span className="detail-label">No. of Requirements</span>
              <span className="detail-value">{jobData.numberOfRequirements || '-'}</span>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div className="detail-item">
              <span className="detail-label">Requirements Period</span>
              <span className="detail-value">{jobData.numberOfRequirementsPeriod || '-'}</span>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div className="detail-item">
              <span className="detail-label">Tentative Start Date</span>
              <span className="detail-value">{formatDate(jobData.tentativeStartDate)}</span>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div className="detail-item">
              <span className="detail-label">Communication Level</span>
              <span className="detail-value">{jobData.communication || '-'}</span>
            </div>
          </Col>
        </Row>
      </Card>

      <Card title={<><EnvironmentOutlined style={{ marginRight: 8 }} />Location & Work Preferences</>} style={{ marginBottom: 24 }}>
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={6}>
            <div className="detail-item">
              <span className="detail-label">Reporting Location</span>
              <span className="detail-value">{jobData.reportingLocation || '-'}</span>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div className="detail-item">
              <span className="detail-label">Worldwide</span>
              <span className="detail-value">{jobData.worldwide ? 'Yes' : 'No'}</span>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div className="detail-item">
              <span className="detail-label">Working Time</span>
              <span className="detail-value">{jobData.workingTime || '-'}</span>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div className="detail-item">
              <span className="detail-label">Working Hours/Week</span>
              <span className="detail-value">{jobData.workingHoursPerWeek || '-'}</span>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div className="detail-item">
              <span className="detail-label">Travel Preference</span>
              <span className="detail-value">{jobData.travelPreference || '-'}</span>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div className="detail-item">
              <span className="detail-label">System Provided</span>
              <span className="detail-value">{jobData.provideSystem || '-'}</span>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div className="detail-item">
              <span className="detail-label">Shift Timing</span>
              <span className="detail-value">{jobData.shiftTiming || '-'}</span>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div className="detail-item">
              <span className="detail-label">Shift Hours</span>
              <span className="detail-value">
                {jobData.shiftStart && jobData.shiftEnd 
                  ? `${jobData.shiftStart} - ${jobData.shiftEnd}` 
                  : '-'}
              </span>
            </div>
          </Col>
        </Row>
      </Card>

      <Card title={<><ToolOutlined style={{ marginRight: 8 }} />Tools & Responsibilities</>}>
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12}>
            <div className="detail-item">
              <span className="detail-label">Tools Used</span>
              <div style={{ marginTop: 4 }}>
                {(jobData.toolsUsed || []).map((tool, index) => (
                  <Tag key={index} color="orange" style={{ marginBottom: 4 }}>
                    {tool}
                  </Tag>
                ))}
                {(!jobData.toolsUsed || jobData.toolsUsed.length === 0) && (
                  <span style={{ color: "#8c8c8c" }}>None specified</span>
                )}
              </div>
            </div>
          </Col>
        </Row>
        
        {jobData.jobResponsibilities && (
          <>
            <Divider />
            <div className="detail-item">
              <span className="detail-label">Job Responsibilities</span>
              <div 
                dangerouslySetInnerHTML={{ __html: renderJobResponsibilities(jobData.jobResponsibilities) }}
                style={{ color: "#595959", lineHeight: 1.8, marginTop: 8 }}
              />
            </div>
          </>
        )}
      </Card>
    </ViewJobDetailsWrapper>
  );
};

export default CustomerJobFullDetails;
